import React, { useState } from 'react';
import { 
  IoPhonePortraitOutline, 
  IoFlashOutline, 
  IoLocationOutline, 
  IoConstructOutline,
  IoHelpCircleOutline,
  IoSearchOutline,
  IoGameControllerOutline,
  IoBuildOutline,
  IoPersonOutline,
  IoTrophyOutline,
  IoHandLeftOutline,
  IoBulbOutline,
  IoMailOutline,
  IoChevronDownOutline
} from 'react-icons/io5';
import './Support.css';

const Support = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const faqData = [
    {
      category: 'gameplay',
      question: 'How does the matching point system work?',
      answer: 'Points are awarded based on how many other players give the same answer as you. If 1 person answers "blue", they get 1 point. If 3 people answer "blue", each gets 3 points. The goal is to think like other players!'
    },
    {
      category: 'gameplay',
      question: 'Can I change my answer after submitting?',
      answer: 'No, once you submit an answer, it cannot be changed. Make sure you\'re confident in your response before clicking submit.'
    },
    {
      category: 'technical',
      question: 'Why am I not seeing questions?',
      answer: 'Make sure you\'re logged in and that your internet connection is stable. If questions still don\'t load, try refreshing the page or clearing your browser cache.'
    },
    {
      category: 'technical',
      question: 'The game is running slowly, what can I do?',
      answer: 'Try closing other browser tabs, ensuring you have a stable internet connection, or using a different browser. Chrome and Firefox work best with our game.'
    },
    {
      category: 'account',
      question: 'How do I reset my password?',
      answer: 'On the login page, click "Forgot Password" and enter your email. You\'ll receive instructions to reset your password.'
    },
    {
      category: 'account',
      question: 'Can I delete my account?',
      answer: 'Yes, contact us through the Contact page and we\'ll help you delete your account and all associated data.'
    },
    {
      category: 'scoring',
      question: 'How are points calculated in the leaderboard?',
      answer: 'Your total score is the sum of all points earned across all games. Each game session contributes to your overall ranking on the daily leaderboard.'
    },
    {
      category: 'scoring',
      question: 'When does the leaderboard reset?',
      answer: 'The leaderboard resets daily at midnight UTC. Your all-time stats are preserved, but the daily rankings start fresh.'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Topics', icon: IoSearchOutline },
    { id: 'gameplay', name: 'Gameplay', icon: IoGameControllerOutline },
    { id: 'technical', name: 'Technical', icon: IoBuildOutline },
    { id: 'account', name: 'Account', icon: IoPersonOutline },
    { id: 'scoring', name: 'Scoring', icon: IoTrophyOutline }
  ];

  const filteredFAQs = faqData.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="support-page">
      <div className="support-container">
        <div className="support-header">
          <h1><IoPhonePortraitOutline className="page-header-icon" /> Support Center</h1>
          <p>Find answers to common questions and get help with AI Game Show</p>
        </div>

        <div className="quick-help">
          <div className="help-card">
            <IoFlashOutline className="help-icon" />
            <h3>Quick Start</h3>
            <p>New to the game? Learn the basics and start playing in minutes.</p>
          </div>
          <div className="help-card">
            <IoLocationOutline className="help-icon" />
            <h3>Game Strategy</h3>
            <p>Tips and tricks to maximize your score and climb the leaderboard.</p>
          </div>
          <div className="help-card">
            <IoConstructOutline className="help-icon" />
            <h3>Troubleshooting</h3>
            <p>Having issues? Check our troubleshooting guide for solutions.</p>
          </div>
        </div>

        <div className="faq-section">
          <h2><IoHelpCircleOutline className="section-icon" /> Frequently Asked Questions</h2>
          
          <div className="faq-controls">
            <div className="search-box">
              <IoSearchOutline className="search-icon" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="category-filters">
              {categories.map(category => {
                const IconComponent = category.icon;
                return (
                  <button
                    key={category.id}
                    className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    <IconComponent className="category-icon" />
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="faq-list">
            {filteredFAQs.length > 0 ? (
              filteredFAQs.map((faq, index) => (
                <FAQItem key={index} faq={faq} />
              ))
            ) : (
              <div className="no-results">
                <IoSearchOutline className="no-results-icon" />
                <h3>No results found</h3>
                <p>Try adjusting your search or category filter</p>
              </div>
            )}
          </div>
        </div>

        <div className="contact-support">
          <h2><IoHandLeftOutline className="section-icon" /> Still Need Help?</h2>
          <p>Can't find what you're looking for? Our support team is here to help!</p>
          <div className="support-actions">
            <a href="/contact" className="support-btn primary">
              <IoMailOutline className="btn-icon" />
              Contact Support
            </a>
            <a href="/feedback" className="support-btn secondary">
              <IoBulbOutline className="btn-icon" />
              Send Feedback
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQItem = ({ faq }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button 
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="question-text">{faq.question}</span>
        <IoChevronDownOutline className={`toggle-icon ${isOpen ? 'rotated' : ''}`} />
      </button>
      {isOpen && (
        <div className="faq-answer">
          <p>{faq.answer}</p>
        </div>
      )}
    </div>
  );
};

export default Support; 